import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
    alpha,
} from '@mui/material';
import {
    Close as CloseIcon,
    Refresh as RefreshIcon,
    DescriptionOutlined as DocIcon,
    WarningAmberOutlined as WarningIcon,
} from '@mui/icons-material';
import {
    DocCoverageStats,
    DocStatus,
    DocTypeCoverage,
    WorkersByDocStatusResponse,
    fetchDocCoverageStats,
    fetchWorkersByDocStatus,
} from '../api/documentAssessmentApi';
import { extractErrorMessageSync } from '../utils/errorHandler';

/**
 * Document Assessment — exposes the EasyFlex document classification that
 * lives in central-db. Coverage overview per doc type + click-through to the
 * actual workers behind each number (valid / expired / missing).
 *
 * Read-only: all data is sourced from central-db via optima-backend proxies
 * (`/workers/easyflex/documents/stats` + `/workers-by-status`).
 */

// Doc types that carry a real expiry/compliance signal — expired counts on
// these are surfaced as actionable warnings.
const EXPIRY_RELEVANT = new Set(['ID_DOCUMENT', 'RIJBEWIJS', 'VCA', 'CERTIFICATE', 'RESIDENCE_PERMIT', 'DIPLOMA', 'A1_POSTING', 'INSURANCE']);

const fmtDate = (v: string | null): string => {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('nl-NL', { year: 'numeric', month: 'short', day: 'numeric' });
};

const DocumentAssessmentPage: React.FC = () => {
    const theme = useTheme();
    const [stats, setStats] = useState<DocCoverageStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Drill-down dialog state
    const [drill, setDrill] = useState<{ type: string; status: DocStatus } | null>(null);
    const [drillData, setDrillData] = useState<WorkersByDocStatusResponse | null>(null);
    const [drillLoading, setDrillLoading] = useState(false);
    const [drillError, setDrillError] = useState<string | null>(null);

    const loadStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setStats(await fetchDocCoverageStats());
        } catch (err) {
            setError(extractErrorMessageSync(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const openDrill = useCallback(async (type: string, status: DocStatus) => {
        setDrill({ type, status });
        setDrillData(null);
        setDrillError(null);
        setDrillLoading(true);
        try {
            setDrillData(await fetchWorkersByDocStatus(type, status));
        } catch (err) {
            setDrillError(extractErrorMessageSync(err));
        } finally {
            setDrillLoading(false);
        }
    }, []);

    const total = stats?.totalWorkers ?? 0;

    const rows = useMemo(() => {
        if (!stats) return [];
        return stats.byType.map((t) => {
            const expired = Math.max(0, t.workersWithAny - t.workersWithValid);
            const coverage = total > 0 ? Math.round((t.workersWithAny / total) * 100) : 0;
            return { ...t, expired, coverage };
        });
    }, [stats, total]);

    const CountLink: React.FC<{ value: number; type: string; status: DocStatus; warn?: boolean }> = ({
        value,
        type,
        status,
        warn,
    }) => {
        if (value <= 0) return <Typography variant="body2" color="text.disabled">0</Typography>;
        return (
            <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => openDrill(type, status)}
                sx={{ fontWeight: 700, color: warn ? theme.palette.warning.main : theme.palette.primary.main }}
            >
                {value}
            </Link>
        );
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                <DocIcon color="primary" />
                <Typography variant="h5" fontWeight={800}>Document Assessment</Typography>
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Refresh">
                    <IconButton onClick={loadStats} disabled={loading}><RefreshIcon /></IconButton>
                </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                EasyFlex documents classified by AI (central-db). {stats ? `${total} assessed workers in ${stats.organizationName}.` : ''}
                {' '}Click a number to see the workers behind it.
            </Typography>

            {loading && <LinearProgress sx={{ mb: 2 }} />}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {stats && (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.06) } }}>
                                <TableCell>Document type</TableCell>
                                <TableCell align="right">Docs</TableCell>
                                <TableCell align="right">Workers with</TableCell>
                                <TableCell align="right">Valid</TableCell>
                                <TableCell align="right">Expired</TableCell>
                                <TableCell align="right">Missing</TableCell>
                                <TableCell align="right">Coverage</TableCell>
                                <TableCell align="center">Recruitment</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => {
                                const warnExpiry = EXPIRY_RELEVANT.has(r.inferredDocType) && r.expired > 0;
                                return (
                                    <TableRow key={r.inferredDocType} hover>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                {warnExpiry && (
                                                    <Tooltip title={`${r.expired} worker(s) with an expired ${r.inferredDocType}`}>
                                                        <WarningIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                                                    </Tooltip>
                                                )}
                                                <Typography variant="body2" fontWeight={600}>{r.inferredDocType}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">{r.documents}</TableCell>
                                        <TableCell align="right">{r.workersWithAny}</TableCell>
                                        <TableCell align="right"><CountLink value={r.workersWithValid} type={r.inferredDocType} status="valid" /></TableCell>
                                        <TableCell align="right"><CountLink value={r.expired} type={r.inferredDocType} status="expired" warn /></TableCell>
                                        <TableCell align="right">
                                            <Link
                                                component="button"
                                                type="button"
                                                variant="body2"
                                                onClick={() => openDrill(r.inferredDocType, 'missing')}
                                            >
                                                view
                                            </Link>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight={700} color={r.coverage >= 80 ? 'success.main' : r.coverage >= 50 ? 'warning.main' : 'error.main'}>
                                                {r.coverage}%
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {r.recruitmentRelevant > 0
                                                ? <Chip size="small" label={r.recruitmentRelevant} color="info" variant="outlined" />
                                                : <Typography variant="caption" color="text.disabled">—</Typography>}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Missing is not in /stats (needs the roster) — render as a clickable "view" per row */}
            {stats && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    "Missing" fetches on click (who among assessed workers has no doc of that type). Coverage % = workers with ≥1 of that type ÷ assessed workers.
                </Typography>
            )}

            <Dialog open={!!drill} onClose={() => setDrill(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h6" fontWeight={700}>
                            {drill?.type} — {drill?.status}
                        </Typography>
                        <Chip size="small" label={`${drillData?.count ?? 0} workers`} />
                        <Box sx={{ flex: 1 }} />
                        <IconButton size="small" onClick={() => setDrill(null)}><CloseIcon fontSize="small" /></IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {drillLoading && <LinearProgress />}
                    {drillError && <Alert severity="error">{drillError}</Alert>}
                    {!drillLoading && !drillError && drillData && (
                        drillData.workers.length === 0
                            ? <Typography variant="body2" color="text.secondary">No workers in this bucket.</Typography>
                            : (
                                <Stack spacing={1}>
                                    {drillData.workers.map((w) => (
                                        <Paper key={w.workerRegistrationNumber} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="body2" fontWeight={700}>
                                                    {w.firstName || ''} {w.lastName || ''}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">regnum {w.workerRegistrationNumber}</Typography>
                                            </Stack>
                                            {w.documents.length > 0 && (
                                                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                                    {w.documents.map((d) => (
                                                        <Chip
                                                            key={d.easyflexDocumentNumber}
                                                            size="small"
                                                            variant="outlined"
                                                            label={`${d.fileName || `#${d.easyflexDocumentNumber}`}${d.validUntil || d.inferredValidUntil ? ` · exp ${fmtDate(d.validUntil || d.inferredValidUntil)}` : ''}`}
                                                            sx={{ maxWidth: 320 }}
                                                        />
                                                    ))}
                                                </Stack>
                                            )}
                                        </Paper>
                                    ))}
                                </Stack>
                            )
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default DocumentAssessmentPage;
